import { SECTIONS, storeKeyFor } from './meetingSections.js';
import { getExtractionVerification } from './assessmentSelectors.js';

// The send version: what the household is given to keep.
//
// Print used to be an output action on the report: whatever was on the page,
// minus the controls, went to the printer. The advisor lead asked for a review
// step, and the proposal was specific about what the review has to show: the
// sections included, the period the goal sheet reads from, the boards, the
// dates the figures rest on, and what the packet cannot say. So the preview is
// the packet itself, exactly as it will print, with that summary above it, and
// print is offered only for a version the advisor has looked at and marked
// reviewed. Any change to a fact, a board, a choice or the period after that
// makes it a new draft, and the page says so rather than printing the old
// approval over new numbers.
//
// Everything here is decided from the view the page already computed and the
// choices in the store, so it can be tested without a browser.

/** The set of section ids in the packet, given the advisor's overrides. */
export function sendIncludes(overrides) {
  const chosen = overrides && typeof overrides === 'object' ? overrides : {};
  const out = new Set();
  for (const s of SECTIONS) {
    const choice = chosen[storeKeyFor(s.id)];
    // The packet starts as the meeting: the same facts, sent home.
    if (choice === true || (choice !== false && s.meet)) out.add(s.id);
  }
  return out;
}

// A short, stable fingerprint of a string. Not cryptographic; it only has to
// change when the packet changes and stay put when it does not.
function fingerprint(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i += 1) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

const boardState = sketch => {
  if (!sketch) return null;
  if (typeof sketch === 'string') return 'picture';
  return [Array.isArray(sketch.strokes) ? sketch.strokes.length : 0, Boolean(sketch.guide), Boolean(sketch.legacy)];
};

/**
 * What a reviewed version is a review OF. Every fact the packet shows, every
 * choice that shapes it, and the boards, reduced to a fingerprint. If any of
 * it changes, the fingerprint changes and the review no longer applies.
 */
export function sendDigest({ sections, period, view, sketches }) {
  const gs = view?.goalSheet?.income || {};
  const bundle = view?.bundle || null;
  const facts = {
    sections: [...(sections || [])].sort(),
    period: period || gs.period || null,
    goalSheet: {
      atYear: gs.atYear ?? null,
      requiredToday: gs.requiredToday ?? null,
      requiredAtRetirement: gs.requiredAtRetirement ?? null,
      totalIncome: gs.totalIncome ?? null,
      shortfall: gs.shortfall ?? null,
    },
    liquid: view?.taxPools?.total ?? null,
    gapNow: view?.summary?.gapNow ?? null,
    depletionYear: view?.summary?.coverage?.depletionYear ?? null,
    horizon: view?.horizon?.year ?? null,
    bundle: bundle ? [bundle.asOf || null, bundle.source || null, (bundle.holdings || []).length] : null,
    unpriced: view?.unpriced ?? null,
    boards: { cashflow: boardState(sketches?.cashflow), tax: boardState(sketches?.tax) },
  };
  return fingerprint(JSON.stringify(facts));
}

/** The period the goal sheet reads from, in the packet summary's words. */
export function periodLabel(income) {
  if (!income) return null;
  if (income.period === 'unresolved') return 'this year, because the questionnaire does not say when retirement is';
  if (income.alreadyThere) return 'now, already retired';
  if (income.period === 'bridge') return `${income.atYear}, the first years of retirement, before every benefit has started`;
  if (income.period === 'steady') return `${income.atYear}, once every benefit has started`;
  return null;
}

const unpricedCount = u => {
  if (!u || typeof u !== 'object') return 0;
  if (typeof u.count === 'number') return u.count;
  for (const k of ['holdings', 'items', 'rows']) if (Array.isArray(u[k])) return u[k].length;
  return 0;
};

/**
 * What the packet cannot say, stated on the preview so the advisor sends it
 * knowingly. A blocking limitation is one the advisor has to resolve first:
 * statements the two reads disagreed on are not client-facing until a person
 * has settled which read was right.
 */
export function sendLimitations({ view, sketches }) {
  const out = [];
  // A packet of zeroes is not a packet. Assets are what the whole report reads
  // from, so their absence blocks sending until they are entered.
  if (view?.hasAssets === false) {
    out.push({
      key: 'no-assets',
      text: 'No assets have been entered, so every figure that reads from them is zero. Add them in the questionnaire before this is sent.',
      blocking: true,
    });
  }
  const unresolved = view?.goalSheet?.income?.unresolvedRetirement || [];
  if (unresolved.length) {
    out.push({
      key: 'retirement',
      text: `The questionnaire does not say when ${unresolved.length === 1 ? 'one of you' : 'either of you'} retires, so section 2 of Goals reads from this year.`,
      blocking: false,
    });
  }
  if (!view?.bundle) {
    out.push({
      key: 'no-statements',
      text: 'No statements were analyzed, so the packet carries no investment analysis: no holdings, fees, style or behaviour.',
      blocking: false,
    });
  } else {
    const v = getExtractionVerification(view.bundle);
    if (v && v.needsReview) {
      out.push({
        key: 'extraction',
        text: 'The two reads of the statements did not agree. Settle that in the extraction review before this is sent.',
        blocking: true,
      });
    }
    const n = unpricedCount(view.unpriced);
    if (n > 0) {
      out.push({
        key: 'unpriced',
        text: `${n} holding${n === 1 ? '' : 's'} could not be priced; the fee and look-through figures cover the rest.`,
        blocking: false,
      });
    }
  }
  for (const [k, name] of [['cashflow', 'cash-flow'], ['tax', 'tax']]) {
    const s = sketches?.[k];
    if (typeof s === 'string' || (s && s.legacy)) {
      out.push({ key: `picture-${k}`, text: `The ${name} board is kept as a picture and prints as one.`, blocking: false });
    }
  }
  return out;
}
